import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Plus, Trash2, Image } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mediaTypes = [
  { value: 'magazine', label: 'Magazine' },
  { value: 'newspaper', label: 'Newspaper' },
  { value: 'journal', label: 'Journal' },
];

const frequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const adPositions = [
  { value: 'front_cover', label: 'Front Cover' },
  { value: 'back_cover', label: 'Back Cover' },
  { value: 'inside_front', label: 'Inside Front' },
  { value: 'inside_back', label: 'Inside Back' },
  { value: 'center_spread', label: 'Center Spread' },
  { value: 'inside_page', label: 'Inside Page' },
  { value: 'premium_position', label: 'Premium Position' },
];

const adSizes = [
  { value: 'full_page', label: 'Full Page' },
  { value: 'half_page', label: 'Half Page' },
  { value: 'quarter_page', label: 'Quarter Page' },
  { value: 'double_spread', label: 'Double Spread' },
  { value: 'strip_ad', label: 'Strip Ad' },
];

const AdminPrintMediaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    media_type: 'magazine',
    description: '',
    logo: '',
    frequency: 'monthly',
    circulation: 0,
    distribution_regions: [],
    language: 'English',
    categories: [],
    target_audience: '',
    artwork_specs: '',
    lead_time_days: 15,
    sample_scan_url: '',
    is_active: true,
    is_featured: false,
    cost_price: '',
    vendor_id: '',
  });
  const [packages, setPackages] = useState([]);
  const [newPackage, setNewPackage] = useState({
    name: '',
    position: 'inside_page',
    size: 'full_page',
    price: '',
    cost_price: '',
    turnaround_days: 15,
    color: true,
  });
  const [regionInput, setRegionInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');

  // Calculate margin preview
  const calculateMargin = (price, cost) => {
    const selling = parseFloat(price) || 0;
    const costPrice = parseFloat(cost) || 0;
    if (selling === 0) return { margin: 0, percent: 0, partnerMargin: 0, isAtRisk: false };
    
    const margin = selling - costPrice;
    const percent = (margin / selling) * 100;
    const partnerMin = selling * 0.82;
    const partnerMargin = partnerMin - costPrice;
    
    return { margin, percent: percent.toFixed(1), partnerMargin, isAtRisk: partnerMargin < 0 };
  };

  useEffect(() => {
    fetchVendors();
    if (isEditing) {
      fetchMedia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/print-media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const item = response.data.find(m => m.id === id);
      if (item) {
        setFormData({
          name: item.name || '',
          media_type: item.media_type || 'magazine',
          description: item.description || '',
          logo: item.logo || '',
          frequency: item.frequency || 'monthly',
          circulation: item.circulation || 0,
          distribution_regions: item.distribution_regions || [],
          language: item.language || 'English',
          categories: item.categories || [],
          target_audience: item.target_audience || '',
          artwork_specs: item.artwork_specs || '',
          lead_time_days: item.lead_time_days || 15,
          sample_scan_url: item.sample_scan_url || '',
          is_active: item.is_active !== false,
          is_featured: item.is_featured || false,
          cost_price: item.cost_price || '',
          vendor_id: item.vendor_id || '',
        });
        setPackages(item.packages || []);
      }
    } catch (error) {
      toast.error('Failed to load media');
      navigate('/admin/print-media');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddRegion = () => {
    if (regionInput.trim() && !formData.distribution_regions.includes(regionInput.trim())) {
      setFormData(prev => ({
        ...prev,
        distribution_regions: [...prev.distribution_regions, regionInput.trim()]
      }));
      setRegionInput('');
    }
  };

  const handleRemoveRegion = (region) => {
    setFormData(prev => ({
      ...prev,
      distribution_regions: prev.distribution_regions.filter(r => r !== region)
    }));
  };

  const handleAddCategory = () => {
    if (categoryInput.trim() && !formData.categories.includes(categoryInput.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryInput.trim()]
      }));
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== cat)
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, logo: response.data.url }));
      toast.success('Logo uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        circulation: parseInt(formData.circulation) || 0,
        lead_time_days: parseInt(formData.lead_time_days) || 15,
      };

      if (isEditing) {
        await axios.put(`${API}/admin/print-media/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Print media updated');
      } else {
        await axios.post(`${API}/admin/print-media`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Print media created');
      }
      
      navigate('/admin/print-media');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPackage = async () => {
    if (!newPackage.name || !newPackage.price) {
      toast.error('Package name and price are required');
      return;
    }

    if (!isEditing) {
      toast.error('Save the media first before adding packages');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/print-packages`, {
        print_media_id: id,
        ...newPackage,
        price: parseFloat(newPackage.price),
        cost_price: parseFloat(newPackage.cost_price) || 0,
        turnaround_days: parseInt(newPackage.turnaround_days) || 15,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPackages([...packages, response.data]);
      setNewPackage({
        name: '',
        position: 'inside_page',
        size: 'full_page',
        price: '',
        cost_price: '',
        turnaround_days: 15,
        color: true,
      });
      toast.success('Package added');
    } catch (error) {
      toast.error('Failed to add package');
    }
  };

  const handleDeletePackage = async (packageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/print-packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(packages.filter(p => p.id !== packageId));
      toast.success('Package deleted');
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl" data-testid="admin-print-media-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/print-media')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">
            {isEditing ? 'Edit Print Media' : 'Add Print Media'}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditing ? 'Update print media details' : 'Add a new magazine, newspaper, or journal'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publication Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Business Today"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                name="media_type"
                value={formData.media_type}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                {mediaTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
              <select
                name="frequency"
                value={formData.frequency}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                {frequencies.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <input
                type="text"
                name="language"
                value={formData.language}
                onChange={handleChange}
                placeholder="e.g., English"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Circulation</label>
              <input
                type="number"
                name="circulation"
                value={formData.circulation}
                onChange={handleChange}
                placeholder="e.g., 100000"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Time (days)</label>
              <input
                type="number"
                name="lead_time_days"
                value={formData.lead_time_days}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Describe the publication, readership, and ad opportunities..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
            <input
              type="text"
              name="target_audience"
              value={formData.target_audience}
              onChange={handleChange}
              placeholder="e.g., Business executives, entrepreneurs aged 25-55"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Artwork Specifications</label>
            <input
              type="text"
              name="artwork_specs"
              value={formData.artwork_specs}
              onChange={handleChange}
              placeholder="e.g., 300 DPI, CMYK, PDF format, 3mm bleed"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            />
          </div>

          {/* Logo Upload */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
            <div className="flex items-center gap-4">
              {formData.logo ? (
                <img src={formData.logo} alt="Logo" className="w-16 h-16 rounded object-cover" />
              ) : (
                <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <label className="px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <span className="text-sm">Upload Logo</span>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        {/* Distribution Regions */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Distribution Regions</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={regionInput}
              onChange={(e) => setRegionInput(e.target.value)}
              placeholder="Add region..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRegion())}
            />
            <button
              type="button"
              onClick={handleAddRegion}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.distribution_regions.map((region, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                {region}
                <button type="button" onClick={() => handleRemoveRegion(region)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Categories</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              placeholder="Add category..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
            />
            <button
              type="button"
              onClick={handleAddCategory}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.categories.map((cat, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                {cat}
                <button type="button" onClick={() => handleRemoveCategory(cat)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Settings</h2>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-[#D4AF37] rounded focus:ring-[#D4AF37]"
              />
              <span className="text-sm">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
                className="w-4 h-4 text-[#D4AF37] rounded focus:ring-[#D4AF37]"
              />
              <span className="text-sm">Featured</span>
            </label>
          </div>
        </div>

        {/* Packages (only for editing) */}
        {isEditing && (
          <div className="bg-white border border-gray-100 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Ad Packages</h2>
            
            {/* Existing Packages */}
            {packages.length > 0 && (
              <div className="space-y-3 mb-6">
                {packages.map(pkg => (
                  <div key={pkg.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-[#1a1a1a]">{pkg.name}</p>
                      <p className="text-sm text-gray-500 capitalize">
                        {pkg.position?.replace(/_/g, ' ')} • {pkg.size?.replace(/_/g, ' ')} • {pkg.turnaround_days} days
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-[#D4AF37]">₹{pkg.price?.toLocaleString()}</span>
                      <button
                        type="button"
                        onClick={() => handleDeletePackage(pkg.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Package */}
            <div className="border border-dashed border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Add New Package</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <input
                  type="text"
                  value={newPackage.name}
                  onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                  placeholder="Package name"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <select
                  value={newPackage.position}
                  onChange={(e) => setNewPackage({ ...newPackage, position: e.target.value })}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  {adPositions.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <select
                  value={newPackage.size}
                  onChange={(e) => setNewPackage({ ...newPackage, size: e.target.value })}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  {adSizes.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <input
                  type="number"
                  value={newPackage.price}
                  onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                  placeholder="Price (₹)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <input
                  type="number"
                  value={newPackage.cost_price}
                  onChange={(e) => setNewPackage({ ...newPackage, cost_price: e.target.value })}
                  placeholder="Cost (₹)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <input
                  type="number"
                  value={newPackage.turnaround_days}
                  onChange={(e) => setNewPackage({ ...newPackage, turnaround_days: e.target.value })}
                  placeholder="TAT (days)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newPackage.color}
                    onChange={(e) => setNewPackage({ ...newPackage, color: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Color</span>
                </label>
                <button
                  type="button"
                  onClick={handleAddPackage}
                  className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] text-sm"
                >
                  Add Package
                </button>
              </div>
              {/* Margin Preview */}
              {newPackage.price && newPackage.cost_price && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${calculateMargin(newPackage.price, newPackage.cost_price).isAtRisk ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  Margin: ₹{calculateMargin(newPackage.price, newPackage.cost_price).margin.toLocaleString()} ({calculateMargin(newPackage.price, newPackage.cost_price).percent}%)
                  {calculateMargin(newPackage.price, newPackage.cost_price).isAtRisk && ' ⚠️ Loss at partner pricing'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vendor Selection */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Cost & Vendor</h2>
          <p className="text-sm text-gray-500 mb-4">Track costs for margin analysis</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Cost Price (₹)</label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                placeholder="What you pay to the publication"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                <option value="">Select vendor</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <a href="/admin/vendors" className="text-xs text-[#D4AF37] hover:underline mt-1 inline-block">Manage vendors →</a>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/print-media')}
            className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Save Changes' : 'Create Media'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPrintMediaForm;
